/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { DbgPaths } from "../../../../../utils/Paths"
import React, { FC } from "react"
import { FormattedMessage } from "react-intl"
import { TextLink } from "@carrotfertility/carotene"
import { Benefit } from "../../../../dynamic-content/Benefit"
import { CoverageConfiguration } from "../../../../../types/benefitConfiguration"
import { UnorderedListItem } from "@carrotfertility/carotene-core"

interface OtherExpensesListItemsProps {
  benefitInfo: Benefit
  country: string
  lineItemFilter: string
  companyCoverage: CoverageConfiguration
  hasAdoptionCategory: boolean
}

export const OtherExpensesHeader: FC = () => {
  return (
    <p>
      <FormattedMessage
        defaultMessage="This category includes the following when you work with a <link>Carrot-eligible provider</link>:"
        values={{
          link: (linkContent) => (
            <TextLink size="medium" target="_blank" href={DbgPaths.PROVIDER_ELIGIBILITY}>
              {linkContent}
            </TextLink>
          )
        }}
      />
    </p>
  )
}

export const OtherExpensesListItems: FC<OtherExpensesListItemsProps> = ({
  benefitInfo,
  country,
  lineItemFilter,
  companyCoverage,
  hasAdoptionCategory
}) => {
  const preservation = benefitInfo.journeys.fertilityPreservation
  const fertilityCare = benefitInfo.journeys.fertilityCare
  const hasElectiveFertilityLineItemFilter = lineItemFilter === "ELECTIVE_FERTILITY"
  const menopause = benefitInfo.journeys.menopause
  return (
    <>
      {companyCoverage.isMemberAndPartnerEligible && !companyCoverage.areLegalPartnersEligible && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Any <link>eligible care and services</link> received by the member’s <link>Carrot-eligible partner</link>, if not legally married or tax-dependent"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.CARROT_ELIGIBLE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {fertilityCare.eligibleExpensesDetail.isElectiveCareCovered && !hasElectiveFertilityLineItemFilter && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>fertility care</link> expenses not related to an infertility diagnosis issued by a fertility doctor"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {fertilityCare.eligibleExpensesDetail.hasDonorAssistance && !hasElectiveFertilityLineItemFilter && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>donor assistance</link> expenses related to fertility care, with or without an infertility diagnosis"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {fertilityCare.eligibleExpensesDetail.hasDonorAssistance && hasElectiveFertilityLineItemFilter && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>donor assistance</link> expenses related to fertility care"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.FERTILITY_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {preservation.eligibleExpensesDetail.isElectivePreservationCovered && !hasElectiveFertilityLineItemFilter && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>fertility preservation</link> expenses not related to an infertility diagnosis or medical necessity, as determined by a fertility doctor"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {preservation.eligibleExpensesDetail.isElectivePreservationCovered &&
        preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered &&
        hasElectiveFertilityLineItemFilter && (
          <UnorderedListItem>
            <p className="lh-copy">
              <FormattedMessage
                defaultMessage="Eligible <link>fertility preservation</link> storage expenses after the first year"
                values={{
                  link: (linkContent) => (
                    <TextLink size="medium" target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                      {linkContent}
                    </TextLink>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )}
      {preservation.eligibleExpensesDetail.isElectivePreservationCovered &&
        preservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered &&
        !hasElectiveFertilityLineItemFilter && (
          <UnorderedListItem>
            <p className="lh-copy">
              <FormattedMessage
                defaultMessage="Eligible <link>fertility preservation</link> storage expenses after the first year, with or without an infertility diagnosis or medical necessity"
                values={{
                  link: (linkContent) => (
                    <TextLink size="medium" target="_blank" href={DbgPaths.PRESERVATION_COVERAGE}>
                      {linkContent}
                    </TextLink>
                  )
                }}
              />
            </p>
          </UnorderedListItem>
        )}
      {benefitInfo.journeys.adoption.hasEligibleExpenses && !hasAdoptionCategory && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>adoption</link> expenses"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.ADOPTION_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {benefitInfo.journeys.gestationalCarrier.hasEligibleExpenses && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>gestational surrogacy</link> expenses, including related donor assistance"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.GESTATIONAL_SURROGACY_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {benefitInfo.journeys.pregnancy.hasEligibleExpenses && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible <link>pregnancy and postpartum</link> expenses"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.PREGNANCY_COVERAGE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {menopause.hasEligibleExpenses && ["US", "AU"].includes(country) && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible Eight Sleep and Perfectly Snug products for <link>menopause</link>"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.MENOPAUSE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
      {menopause.hasEligibleExpenses && ["GB", "DE"].includes(country) && (
        <UnorderedListItem>
          <p className="lh-copy">
            <FormattedMessage
              defaultMessage="Eligible Eight Sleep products for <link>menopause</link>"
              values={{
                link: (linkContent) => (
                  <TextLink size="medium" target="_blank" href={DbgPaths.MENOPAUSE}>
                    {linkContent}
                  </TextLink>
                )
              }}
            />
          </p>
        </UnorderedListItem>
      )}
    </>
  )
}
